import { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Paperclip, X, FileText, User, Settings2, GraduationCap, Library, BookOpen, MessageSquarePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const EXAMPLES = [
  "How do I break into Investment Banking?",
  "Explain DCF in simple terms.",
  "Help me prepare for a Private Equity interview.",
  "Review my resume.",
  "Compare Investment Banking vs Private Equity.",
  "Build a 90-day Investment Banking roadmap.",
  "Recommend finance learning resources.",
];

const MODELS = [
  { id: "anthropic", label: "VantStride AI · Precise", model: "claude-sonnet-4-5-20250929" },
  { id: "openai", label: "VantStride AI · Fast", model: "gpt-5.2" },
];

const ECOSYSTEM_LINKS = [
  { label: "Career Hubs", to: "/roadmaps", icon: GraduationCap },
  { label: "Ebooks", to: "/ebooks", icon: Library },
  { label: "Premium Playbooks", to: "/playbooks", icon: BookOpen },
];

function renderMarkdown(text) {
  if (!text) return "";
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const lines = text.split("\n");
  let html = "";
  let inList = false, inOl = false;
  const closeLists = () => {
    if (inList) { html += "</ul>"; inList = false; }
    if (inOl) { html += "</ol>"; inOl = false; }
  };
  for (let raw of lines) {
    let line = escape(raw);
    line = line.replace(/`([^`]+)`/g, "<code>$1</code>");
    line = line.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    line = line.replace(/(^|\s)\*([^*\n]+)\*/g, "$1<em>$2</em>");
    if (/^\s*### /.test(raw)) { closeLists(); html += `<h3>${line.replace(/^\s*###\s*/, "")}</h3>`; continue; }
    if (/^\s*## /.test(raw)) { closeLists(); html += `<h2>${line.replace(/^\s*##\s*/, "")}</h2>`; continue; }
    if (/^\s*# /.test(raw)) { closeLists(); html += `<h1>${line.replace(/^\s*#\s*/, "")}</h1>`; continue; }
    if (/^\s*[-*]\s+/.test(raw)) {
      if (!inList) { closeLists(); html += "<ul>"; inList = true; }
      html += `<li>${line.replace(/^\s*[-*]\s+/, "")}</li>`;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(raw)) {
      if (!inOl) { closeLists(); html += "<ol>"; inOl = true; }
      html += `<li>${line.replace(/^\s*\d+\.\s+/, "")}</li>`;
      continue;
    }
    if (raw.trim() === "") { closeLists(); continue; }
    closeLists();
    html += `<p>${line}</p>`;
  }
  closeLists();
  return html;
}

export default function CareerAI() {
  const { user, loading: authLoading, openAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialConv = params.get("c") || null;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [attachment, setAttachment] = useState(null); // {filename, content_type, data_base64}
  const fileInputRef = useRef(null);
  const [sessionId, setSessionId] = useState(initialConv);
  const [providerIdx, setProviderIdx] = useState(0);
  const scrollRef = useRef(null);
  // Tracks any sessionId that was created locally via `send()`. When the stream
  // returns a new `session_id`, we call `setParams({c: sessionId})` which
  // mutates `initialConv`. Without this guard, the "load prior conversation"
  // effect below would re-run on our own URL change and wipe the in-flight
  // assistant bubble.
  const localSessionsRef = useRef(new Set());

  // Load prior conversation if ?c= provided and user is logged in.
  // Skip loading when the session was just created locally in this component.
  useEffect(() => {
    if (!initialConv || !user) return;
    if (localSessionsRef.current.has(initialConv)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/user/conversations/${initialConv}/messages`);
        if (cancelled) return;
        const msgs = (data.messages || []).map((m) => ({
          id: m.id, role: m.role, content: m.content, attachment_name: m.attachment_name || null,
        }));
        setMessages(msgs);
        setSessionId(initialConv);
      } catch (_) {
        toast.error("Could not load that conversation.");
      }
    })();
    return () => { cancelled = true; };
  }, [initialConv, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const startNew = () => {
    setMessages([]);
    setSessionId(null);
    setParams({}, { replace: true });
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okTypes = ["application/pdf", "image/png", "image/jpeg"];
    const type = okTypes.includes(file.type)
      ? file.type
      : /\.jpe?g$/i.test(file.name) ? "image/jpeg"
      : /\.png$/i.test(file.name) ? "image/png"
      : /\.pdf$/i.test(file.name) ? "application/pdf"
      : null;
    if (!type) {
      toast.error("Please upload a PDF, JPG, or PNG file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File is too large. Please keep it under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setAttachment({ filename: file.name, content_type: type, data_base64: b64 });
    };
    reader.onerror = () => toast.error("Couldn't read that file. Please try again.");
    reader.readAsDataURL(file);
  };

  const send = async (text) => {
    let msg = (text ?? input).trim();
    const att = attachment;
    if (streaming || (!msg && !att)) return;
    if (!msg && att) msg = "Please review the attached document.";
    setInput("");
    setAttachment(null);
    const userMsg = {
      role: "user",
      content: msg,
      id: crypto.randomUUID(),
      attachment_name: att?.filename || null,
    };
    const asstMsg = { role: "assistant", content: "", id: crypto.randomUUID(), pending: true };
    setMessages((m) => [...m, userMsg, asstMsg]);
    setStreaming(true);
    const provider = MODELS[providerIdx];

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: msg,
          provider: provider.id,
          model: provider.model,
          attachment: att || undefined,
        }),
      });
      if (!res.ok || !res.body) {
        let detail = "";
        try {
          const errBody = await res.json();
          detail = errBody?.detail || "";
        } catch (_) {}
        if (res.status === 400 && detail) {
          const err = new Error(detail);
          err.isClientDetail = true;
          throw err;
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.type === "session" && evt.session_id) {
              localSessionsRef.current.add(evt.session_id);
              setSessionId(evt.session_id);
              if (user) setParams({ c: evt.session_id }, { replace: true });
            } else if (evt.type === "delta") {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, content: last.content + evt.content, pending: false };
                }
                return copy;
              });
            } else if (evt.type === "error") {
              toast.error(evt.message || "Something went wrong");
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      toast.error(e?.isClientDetail ? e.message : "Something went wrong while generating a response. Please try again.");
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) copy.pop();
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">VantStride AI</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#0A2540] font-medium mt-2">
            Your finance career assistant.
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed" data-testid="ai-description">
            Reading a hub or working through a playbook and something doesn't click? Ask it here instead of opening another tab. Recruiting, interviews, valuation, modelling — or anything else you hit while preparing.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="ai-ecosystem-links">
            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Explore the platform
            </span>
            {ECOSYSTEM_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`ai-ecosystem-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 text-xs text-slate-700 border border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50 rounded-full px-2.5 py-1.5 transition-colors"
              >
                <l.icon className="w-3 h-3 text-[#0A2540]" />
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 self-start">
          <button
            type="button"
            onClick={startNew}
            data-testid="ai-new-chat"
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#0A2540] border border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50 rounded-full px-3 py-2 transition-colors"
          >
            <MessageSquarePlus className="w-3.5 h-3.5" /> New chat
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="ai-settings-trigger"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0A2540] border border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50 rounded-full px-3 py-2 transition-colors"
              aria-label="AI settings"
            >
              <Settings2 className="w-3.5 h-3.5" /> AI settings
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                Response style
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {MODELS.map((m, i) => (
                <DropdownMenuItem
                  key={m.id}
                  onClick={() => setProviderIdx(i)}
                  data-testid={`ai-model-${m.id}`}
                  className="cursor-pointer text-sm"
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${i === providerIdx ? "bg-[#0A2540]" : "bg-slate-300"}`} />
                  {m.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!authLoading && !user && (
        <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Save className="w-4 h-4 text-[#C6A15B]" />
            <span>Create a free account to save conversations and continue them anytime.</span>
          </div>
          <button
            type="button"
            onClick={() => openAuth({ tab: "signup" })}
            data-testid="ai-signup-cta"
            className="whitespace-nowrap text-xs font-semibold bg-[#0A2540] text-white rounded-full px-3.5 py-2 hover:bg-[#051A2E] transition-colors"
          >
            Create Free Account
          </button>
        </div>
      )}

      <div
        className="mt-6 rounded-[2rem] border border-slate-100 bg-white shadow-[0_20px_60px_rgba(10,37,64,0.06)] overflow-hidden flex flex-col"
        style={{ minHeight: "60vh" }}
        data-testid="chat-panel"
      >
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 bg-slate-50/40"
          style={{ maxHeight: "60vh" }}
          data-testid="chat-scroll"
        >
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0A2540] text-white grid place-items-center">
                <img src="/vs-monogram.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" />
              </div>
              <h3 className="font-display text-2xl text-[#0A2540] font-medium mt-5">
                Start with a finance question
              </h3>
              <p className="text-slate-600 mt-2">Try one of the prompts below or ask your own.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => {
                      if (ex === "Review my resume.") {
                        setInput(ex);
                        toast.message("Attach your resume (PDF, JPG, or PNG) with the paperclip below, then hit send.");
                        return;
                      }
                      send(ex);
                    }}
                    data-testid={`example-prompt-${i}`}
                    className="text-left text-sm rounded-2xl border border-slate-200 bg-white hover:border-[#0A2540]/30 hover:bg-slate-50 px-4 py-3 transition-colors text-slate-700"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-msg-${m.role}`}
            >
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-[#0A2540] text-white grid place-items-center flex-shrink-0">
                  <img src="/vs-monogram.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain" />
                </div>
              )}
              <div
                className={`max-w-[85%] sm:max-w-[75%] text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#0A2540] text-white rounded-2xl rounded-tr-sm p-4"
                    : "bg-white border border-slate-100 text-[#0F172A] rounded-2xl rounded-tl-sm p-4 shadow-sm"
                }`}
              >
                {m.role === "user" && m.attachment_name && (
                  <div className="mb-2 flex justify-end">
                    <span
                      data-testid="chat-attachment-chip"
                      className="inline-flex items-center gap-1.5 text-[11px] bg-white/10 text-white/90 rounded-full px-2.5 py-1"
                    >
                      <FileText className="w-3 h-3" />
                      {m.attachment_name}
                    </span>
                  </div>
                )}
                {m.role === "assistant" ? (
                  m.pending && !m.content ? (
                    <span className="inline-flex gap-1 text-slate-400">
                      <span className="vestryn-blink">●</span>
                      <span className="vestryn-blink" style={{ animationDelay: "0.2s" }}>●</span>
                      <span className="vestryn-blink" style={{ animationDelay: "0.4s" }}>●</span>
                    </span>
                  ) : (
                    <div className="chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                  )
                ) : (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                )}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 grid place-items-center flex-shrink-0">
                  <User className="w-4 h-4" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="border-t border-slate-100 p-4 bg-white">
          {attachment && (
            <div className="mb-2.5 flex items-center" data-testid="chat-attachment-preview">
              <span className="inline-flex items-center gap-2 text-xs text-[#0A2540] bg-slate-50 border border-slate-200 rounded-full pl-3 pr-2 py-1.5">
                <FileText className="w-3.5 h-3.5 vestryn-accent" />
                <span className="max-w-[220px] truncate">{attachment.filename}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  data-testid="chat-attachment-remove"
                  aria-label="Remove attachment"
                  className="w-5 h-5 grid place-items-center rounded-full hover:bg-slate-200 text-slate-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
          <div className="flex items-end gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className="hidden"
              onChange={onPickFile}
              data-testid="chat-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={streaming}
              data-testid="chat-attach-btn"
              aria-label="Attach a file (PDF, JPG, or PNG)"
              title="Attach your resume (PDF, JPG, or PNG)"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-slate-500 hover:text-[#0A2540] hover:border-[#0A2540]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95 flex-shrink-0"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              placeholder="Ask about IB, PE, VC, HF, S&T, valuation, modeling, resumes, interviews…"
              className="flex-1 resize-none rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white placeholder:text-slate-400"
              data-testid="chat-input"
              disabled={streaming}
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={streaming || (!input.trim() && !attachment)}
              data-testid="chat-send-btn"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#0A2540] text-white hover:bg-[#051A2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-2 text-[11px] text-slate-400">
            VantStride AI is designed specifically for finance careers. Responses may be inaccurate — always verify critical details.
          </div>
        </div>
      </div>
    </div>
  );
}
